import { GeoPoint, densify, destination, unwrapLongitudes } from '../model/geodesy';
import { bandFillMinZoom, placeTicks, tickInterval } from '../model/ticks';
import type { MeasureMeasurement, MeasureSession } from '../model/session';
import type { MeasureCollections } from './MeasureMapLayers';
import type { MeasurePointKind } from './layers';

// What the map draws for the measure tool, as one FeatureCollection per source —
// a port of Android's MeasureMapStateBuilder, so the map is testable without a map.

export const CIRCLE_VERTICES = 128;
export const RAY_MIN_METERS = 20000;
export const RAY_SEGMENTS = 64;

export interface MeasureDrawInput {
  session: MeasureSession;
  measurement: MeasureMeasurement | null;
  /** The cursor's live preview while placing (desktop only). */
  preview: GeoPoint | null;
  /** With Snap to route on, the on-trail path the preview measures along, start first. */
  previewRoute?: GeoPoint[] | null;
  /** While picking the start with Snap to route on: where on the line it would land. */
  startPreview?: GeoPoint | null;
  /** The profile scrub's point on the line. */
  profilePoint: GeoPoint | null;
  toleranceMeters: number;
  zoom: number;
}

export function circle(centre: GeoPoint, radiusMeters: number): GeoPoint[] {
  if (!(radiusMeters > 0)) return [];
  return unwrapLongitudes(Array.from({ length: CIRCLE_VERTICES + 1 }, (_, i) =>
    destination(centre, (360 * (i % CIRCLE_VERTICES)) / CIRCLE_VERTICES, radiusMeters).point));
}

export function ray(start: GeoPoint, bearing: number, lengthMeters: number): GeoPoint[] {
  return unwrapLongitudes(Array.from({ length: RAY_SEGMENTS + 1 }, (_, i) =>
    (i === 0 ? start : destination(start, bearing, (lengthMeters * i) / RAY_SEGMENTS).point)));
}

const coords = (points: GeoPoint[]) => points.map((p) => [p.lng, p.lat]);
const collection = (features: any[]) => ({ type: 'FeatureCollection', features });
const lines = (...candidates: GeoPoint[][]) => collection(candidates.filter((c) => c.length >= 2)
  .map((c) => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords(c) } })));
const point = (p: GeoPoint, kind: MeasurePointKind) =>
  ({ type: 'Feature', properties: { kind }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } });

/** Every measure source's data, plus the zoom from which the band is filled rather than stroked. */
export function measureFeatures(input: MeasureDrawInput): { collections: MeasureCollections; bandFillMinZoom: number } {
  const { session, measurement, preview, profilePoint, toleranceMeters, zoom } = input;
  const start = session.start;
  const empty = { collections: emptyCollections(), bandFillMinZoom: 0 };
  if (!start) {
    if (input.startPreview) empty.collections['measure-points'] = collection([point(input.startPreview, 'preview')]);
    return empty;
  }

  const isMyLocation = session.startSource === 'MyLocation';
  const points: any[] = [];
  if (isMyLocation) points.push(point(start, 'my-location'));
  const collections = emptyCollections();

  if (!measurement) {
    if (!isMyLocation) points.push(point(start, 'start-pin'));
    if (preview) {
      // Snapping: preview the walk along the trail that the panel's live figures measure.
      const along = input.previewRoute && input.previewRoute.length >= 2 ? input.previewRoute : null;
      collections['measure-preview'] = lines(unwrapLongitudes(along ?? densify(start, preview)));
      if (along) points.push(point(along[0], 'route-start'));
      points.push(point(preview, 'preview'));
    }
    collections['measure-points'] = collection(points);
    return { collections, bandFillMinZoom: 0 };
  }

  const routeMode = measurement.routeMode;
  const lock = routeMode ? 'Free' : session.lock;
  const direct = unwrapLongitudes(densify(measurement.start, measurement.target));
  if (!isMyLocation) points.push(point(routeMode ? measurement.start : start, 'start-pin'));
  if (routeMode) points.push(point(measurement.start, 'route-start'));
  if (profilePoint) points.push(point(profilePoint, 'profile'));
  points.push(point(measurement.target, 'tip'));
  collections['measure-points'] = collection(points);

  if (routeMode) {
    collections['measure-route'] = lines(unwrapLongitudes(measurement.routePath || []));
  } else {
    collections['measure-straight'] = lines(direct);
    const interval = tickInterval(zoom, measurement.start.lat);
    collections['measure-ticks'] = collection(placeTicks(measurement.start, measurement.trueBearing, measurement.distance, interval)
      .map((tick: any) => ({ type: 'Feature', properties: { rotation: tick.rotation }, geometry: { type: 'Point', coordinates: [tick.point.lng, tick.point.lat] } })));
  }

  const lockCircle = lock === 'Distance' ? circle(start, session.lockedRange) : [];
  const bearingRay = lock === 'Bearing' ? ray(start, session.lockedBearing, Math.max(RAY_MIN_METERS, 3 * measurement.distance)) : [];
  // No straight guide between the ends in route mode: the distance is the trail's, so a dashed
  // straight line beside it only made the walker ask which of the two it measured.
  collections['measure-guides'] = lines(lockCircle, bearingRay);

  const band = lock === 'Distance' && session.band;
  if (band) {
    const outer = circle(start, session.lockedRange + toleranceMeters);
    const inner = session.lockedRange > toleranceMeters ? circle(start, session.lockedRange - toleranceMeters) : [];
    const rings = [coords(outer), ...(inner.length >= 4 ? [coords([...inner].reverse())] : [])];
    collections['measure-band'] = collection([{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: rings } }]);
    collections['measure-band-line'] = lines(lockCircle);
  }
  return { collections, bandFillMinZoom: band ? bandFillMinZoom(start.lat, toleranceMeters) : 0 };
}

function emptyCollections(): MeasureCollections {
  const none = () => collection([]);
  return {
    'measure-band': none(), 'measure-band-line': none(), 'measure-guides': none(), 'measure-straight': none(),
    'measure-route': none(), 'measure-preview': none(), 'measure-ticks': none(), 'measure-points': none(),
  };
}
